import Axhelper from '@/lib/Axhelper'

class PlacowkaService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			placowkaAll: '/api/placowka/filters',
			placowkaDetails: '/api/placowka/details',
			placowkaSearch: '/api/placowka/filters',
			placowkaCreate: '/api/placowka',
			placowkaUpdate: '/api/placowka',
			placowkaDelete: '/api/placowka',
			placowkaUpdateTriage: '/api/placowka/updatetriage',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_miasto) {
				params.append('id_miasto', String(filters.id_miasto))
			}
			if (filters?.id_gmina) {
				params.append('id_gmina', String(filters.id_gmina))
			}
			if (filters?.id_powiat) {
				params.append('id_powiat', String(filters.id_powiat))
			}
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			if (filters?.id_typ) {
				params.append('id_typ', String(filters.id_typ))
			}
			if (filters?.triage !== null && filters?.triage !== undefined && filters?.triage !== '') {
				params.append('triage', String(filters.triage))
			}
			const url = `${this.endpoints.placowkaAll}?${params.toString()}`

			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('PlacowkaService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch placowki',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(placowkaId) {
		try {
			if (!placowkaId) {
				return {
					data: null,
					error: 'Invalid placowka ID provided',
				}
			}

			const url = `${this.endpoints.placowkaDetails}/${encodeURIComponent(placowkaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('PlacowkaService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch placowka details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = (searchTerm || '').trim()
			if (!trimmed) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_miasto) {
				params.append('id_miasto', String(filters.id_miasto))
			}
			if (filters?.id_gmina) {
				params.append('id_gmina', String(filters.id_gmina))
			}
			if (filters?.id_powiat) {
				params.append('id_powiat', String(filters.id_powiat))
			}
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			if (filters?.id_typ) {
				params.append('id_typ', String(filters.id_typ))
			}
			if (filters?.triage !== null && filters?.triage !== undefined && filters?.triage !== '') {
				params.append('triage', String(filters.triage))
			}
			const url = `${this.endpoints.placowkaSearch}?searchText=${encodeURIComponent(trimmed)}&${params.toString()}`

			const looksLikeObjectId = /^[a-fA-F0-9]{24}$/.test(trimmed)
			const [response, byIdResult] = await Promise.all([
				Axhelper.sendGetAsync(url),
				looksLikeObjectId ? this.getById(trimmed) : Promise.resolve({ data: null, error: null }),
			])

			let rows = []
			if (!response.error) {
				rows = Array.isArray(response.data) ? [...response.data] : []
			}

			if (looksLikeObjectId && byIdResult?.data && !byIdResult.error) {
				const doc = byIdResult.data
				const docId = doc?._id ?? doc?.id
				const already = rows.some(
					item => String(item?._id ?? item?.id) === String(docId),
				)
				if (!already) {
					rows.unshift(doc)
				}
			}

			if (response.error && rows.length === 0) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: rows.length

			return {
				data: rows,
				error: null,
				totalCount,
				hasMore: (rows.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('PlacowkaService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search placowki',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(placowkaData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.placowkaCreate, placowkaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('PlacowkaService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create placowka',
			}
		}
	}

	async update(placowkaId, placowkaData) {
		try {
			if (!placowkaId) {
				return {
					success: false,
					error: 'Invalid placowka ID provided',
				}
			}

			const url = `${this.endpoints.placowkaUpdate}/${encodeURIComponent(placowkaId)}`
			const response = await Axhelper.sendPutAsync(url, placowkaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('PlacowkaService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update placowka',
			}
		}
	}

	async updateTriage(clientId, triage) {
		try {
			if (!clientId) {
				return {
					success: false,
					error: 'Invalid client ID provided',
				}
			}

			const url = `${this.endpoints.placowkaUpdateTriage}/${encodeURIComponent(clientId)}`
			const response = await Axhelper.sendPutAsync(url, { triage })

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('PlacowkaService.updateTriage error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update client triage',
			}
		}
	}

	async delete(placowkaId) {
		try {
			if (!placowkaId) {
				return {
					success: false,
					error: 'Invalid placowka ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.placowkaDelete}/${encodeURIComponent(placowkaId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Placowka deleted successfully',
			}
		} catch (error) {
			console.error('PlacowkaService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete placowka',
				message: null,
			}
		}
	}
}

export default new PlacowkaService()
